import { useState, useEffect } from "react";
import { apiFetch } from "../utils/api";

export function useRoomJoin({ id, userId, user, isDiscord, navigate, authLoading, loginWithGoogle, applyDeltaGameStateRef }) {
  const roomKey = `gameRoom-${id}-userId`;
  const [joinCode, setJoinCode] = useState(
    localStorage.getItem(`${roomKey}-joinCode`) || ""
  );
  const [joinError, setJoinError] = useState("");
  const [isJoining, setIsJoining] = useState(false);
  const [hasJoined, setHasJoined] = useState(false);

  useEffect(() => {
    setHasJoined(false);
    setJoinError("");
  }, [id, userId]);

  useEffect(() => {
    setJoinCode(localStorage.getItem(`${roomKey}-joinCode`) || "");
  }, [roomKey]);

  useEffect(() => {
    if (joinError) {
      setJoinError("");
    }
  }, [joinCode]);

  // Check access on mount
  useEffect(() => {
    if (!id || !userId || hasJoined || authLoading) return;
    let isActive = true;
    const checkAccess = async () => {
      try {
        const response = await apiFetch(`api/gamerooms/${id}/state`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        });
        if (!isActive) return;
        if (response.ok) {
          const data = await response.json();
          applyDeltaGameStateRef.current?.(data, { isFullState: true });
          setHasJoined(true);
          return;
        }
        if (response.status === 404) {
          navigate("/rooms");
        }
      } catch (err) {
        console.warn("Failed to check join status:", err);
      }
    };
    checkAccess();
    return () => {
      isActive = false;
    };
  }, [id, userId, hasJoined, authLoading, navigate]);

  const handleJoinSubmit = async (e) => {
    e?.preventDefault();
    if (!user) {
      loginWithGoogle(`/rooms/${id}`);
      return;
    }
    setJoinError("");
    if (!joinCode) {
      setJoinError("Join code is required");
      return;
    }
    try {
      setIsJoining(true);
      const response = await apiFetch(`api/gamerooms/${id}/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          joinCode: joinCode,
        }),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || "Failed to join game room");
      }
      await response.json();
      localStorage.setItem(`${roomKey}-joinCode`, joinCode);
      setHasJoined(true);

      // Immediately fetch the full state so the new player gets all territories.
      try {
        const fullResp = await apiFetch(`api/gamerooms/${id}/state`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ full: true }),
        });
        if (fullResp.ok) {
          const fullData = await fullResp.json();
          applyDeltaGameStateRef.current?.(fullData, { isFullState: true });
        }
      } catch (err) {
        console.error("Error fetching full state on join:", err);
      }
    } catch (err) {
      console.error("Join error:", err);
      setJoinError(err.message);
    } finally {
      setIsJoining(false);
    }
  };

  return {
    joinCode,
    setJoinCode,
    joinError,
    isJoining,
    hasJoined,
    setHasJoined,
    handleJoinSubmit,
  };
}
